import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  name: string;
  label?: string;
  className?: string;
  wrapperClassName?: string;
  error?: string;
  isTextarea?: boolean;
  description?: string;
  'data-testid'?: string;
}

export default function FormInput({
  id,
  name,
  label,
  className,
  wrapperClassName,
  error,
  isTextarea = false,
  type = 'text',
  description,
  ...otherProps
}: FormInputProps) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div
      className={twMerge([
        'relative flex w-full flex-col items-start justify-start gap-1',
        wrapperClassName,
      ])}
    >
      {label && (
        <label
          htmlFor={id}
          className="block text-left text-sm font-medium leading-6 text-gray-900"
        >
          {label}
        </label>
      )}
      {description && (
        <p className="text-left text-sm text-gray-500">{description}</p>
      )}
      <div className="relative w-full">
        {isTextarea ? (
          <textarea
            id={id}
            name={name}
            type={showPassword ? 'text' : type}
            data-testid={otherProps['data-testid'] || id}
            className={twMerge([
              'block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6',
              className,
              error && 'border-red-500',
            ])}
            {...(otherProps as any)}
          />
        ) : (
          <input
            id={id}
            name={name}
            type={showPassword ? 'text' : type}
            data-testid={otherProps['data-testid'] || id}
            className={twMerge([
              'block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6',
              className,
              error && 'border-red-500',
              type === 'password' && 'pr-10',
            ])}
            {...otherProps}
          />
        )}

        {type === 'password' && (
          <button
            type="button"
            data-testid={
              otherProps['data-testid']
                ? `${otherProps['data-testid']}-toggle-password`
                : `${id}-toggle-password`
            }
            className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <EyeSlashIcon className="h-5 w-5 text-gray-400" />
            )}
          </button>
        )}
      </div>

      {error && (
        <p
          className="text-left text-sm text-red-500"
          id={`${id}-error`}
          data-testid={
            otherProps['data-testid']
              ? `${otherProps['data-testid']}-error`
              : `${id}-error`
          }
        >
          {error}
        </p>
      )}
    </div>
  );
}
